import { apiClient } from '../client';
import type { ApiResponse } from '../types';

// ---------------------------------------------------------------------------
// Types — field names match the .NET DTO exactly, including the "ammount"
// typo that is intentional to preserve wire compatibility.
// ---------------------------------------------------------------------------

export interface DashboardMainByTowProviderDTO {
  open: number;
  openWithoutOffer: number;
  winner: number;
  toArrive: number;
  inProcess: number;
  delivered: number;
}

export interface DashboardWeekByTowProviderDTO {
  towRequest: number;
  offerRequest: number;
  winnerRequest: number;
  /** Preserved typo from backend — wire field is "ammount", not "amount". */
  ammount: string;
}

export interface DashboardMonthByTowProviderDTO {
  towRequest: number;
  offerRequest: number;
  winnerRequest: number;
  /** Preserved typo from backend — wire field is "ammount", not "amount". */
  ammount: string;
}

/**
 * The provider's roadside work. Roadside jobs live in `SrServiceRequest`, a
 * different table from `CmTowRequest`, so none of the `main` counts include
 * any of it.
 */
export interface DashboardRoadsideByTowProviderDTO {
  open: number;
  winner: number;
  toArrive: number;
  inProcess: number;
  delivered: number;
}

/** One week of bidding — tow and roadside offers summed. Buckets are 7 days wide. */
export interface OfferActivityBucketDTO {
  /** ISO date, UTC midnight of the bucket's FIRST day. */
  date: string;
  submitted: number;
  /** Counted by `winnerDate`, so a bid placed in week 1 that wins in week 3 lands in week 3. */
  won: number;
}

export interface ResponseDashboardByTowProviderDTO {
  startDate: string;
  endDate: string | null;
  main: DashboardMainByTowProviderDTO;
  /** Absent on API builds older than the roadside KPI row. */
  roadside?: DashboardRoadsideByTowProviderDTO;
  week: DashboardWeekByTowProviderDTO;
  month: DashboardMonthByTowProviderDTO;
  /** Absent on API builds older than the conversion chart. */
  offerActivity?: OfferActivityBucketDTO[];
}

// ---------------------------------------------------------------------------
// Admin dashboard types — field names match DashboardDTOs.cs exactly,
// including the typos "Whitout", "Assintence", and singular "unreadMessage".
// ---------------------------------------------------------------------------

export interface Dashboard24HoursDTO {
  unreadMessage: number;
  newClaims: number;
  closedClaims: number;
  reopenClaims: number;
  /** Preserved typo from backend — wire field is "towRequestWhitout…". */
  towRequestWhitoutTowProvider: number;
}

export interface DashboardWeekDTO {
  openClaims: number;
  newAdjusters: number;
  newCustomers: number;
  newDrivers: number;
  towRequest: number;
  /** Preserved typo from backend — wire field is "roadSideAssintence". */
  roadSideAssintence: number;
  /** Preserved typo from backend — wire field is "towRequestWhitout…". */
  towRequestWhitoutTowProvider: number;
}

export interface DriverActivityByDayDTO {
  /** ISO date string (UTC midnight) for the day bucket. */
  date: string;
  count: number;
}

export interface ResponseDashboardDTO {
  hours: Dashboard24HoursDTO;
  week: DashboardWeekDTO;
  startDate: string;
  /** Not nullable on this DTO — differs from TowProvider's nullable endDate. */
  endDate: string;
  /**
   * Tow requests created per day, last 7 days. The field keeps its original
   * name for the wire contract, but it has only ever counted tow requests — the
   * UI labels it for what it is rather than "driver activity".
   */
  driverActivity: DriverActivityByDayDTO[];
  /** Roadside assistance jobs completed per day, last 7 days. */
  roadsideActivity: DriverActivityByDayDTO[];
}

// ---------------------------------------------------------------------------
// Endpoint helpers
// ---------------------------------------------------------------------------

/**
 * GET /api/v1/Dashboard/GetByTowProvider
 *
 * Requires Bearer auth (attached automatically by the apiClient interceptor).
 *
 * @returns Unwrapped `ResponseDashboardByTowProviderDTO` — callers do not
 *          need to access `.data` again.
 */
export async function getTowProviderDashboard(): Promise<ResponseDashboardByTowProviderDTO> {
  const response = await apiClient.get<ApiResponse<ResponseDashboardByTowProviderDTO>>(
    '/api/v1/Dashboard/GetByTowProvider',
  );
  return response.data.data;
}

/**
 * GET /api/v1/Dashboard/GetByAdmin?timeOffSet={hours}
 *
 * `timeOffSet` is hours offset from UTC, sign-flipped so east-of-UTC is
 * positive — matches Angular's `fnDate.GetValueTimeZoneOffSet()`.
 *
 * Requires Bearer auth (attached automatically by the apiClient interceptor).
 *
 * @returns Unwrapped `ResponseDashboardDTO` — callers do not need to access
 *          `.data` again.
 */
export async function getAdminDashboard(timeOffSet: number): Promise<ResponseDashboardDTO> {
  const response = await apiClient.get<ApiResponse<ResponseDashboardDTO>>(
    '/api/v1/Dashboard/GetByAdmin',
    { params: { timeOffSet } },
  );
  return response.data.data;
}

// ---------------------------------------------------------------------------
// Adjuster dashboard types — field names match DashboardDTOs.cs exactly.
// No preserved typos on this DTO; backend names are clean.
// ---------------------------------------------------------------------------

export interface DashboardMainByAdjusterDTO {
  unreadMessages: number;
  openClaims: number;
  openDocumentRequest: number;
  pendingDocumentRequest: number;
  pendingDocumentRequestLast7Days: number;
}

export interface DashboardWeekByAdjusterDTO {
  newClaims: number;
  closedClaims: number;
  approveDocumentRequest: number;
  rejectedDocumentRequest: number;
}

export interface ResponseDashboardByAdjusterDTO {
  startDate: string;
  /** Nullable on this DTO — mirrors TowProvider's pattern. */
  endDate: string | null;
  main: DashboardMainByAdjusterDTO;
  week: DashboardWeekByAdjusterDTO;
}

/**
 * GET /api/v1/Dashboard/GetByAdjuster
 *
 * No query params. Requires Bearer auth (attached automatically by the
 * apiClient interceptor). Backend restricts this route to ADJUSTER userType.
 *
 * @returns Unwrapped `ResponseDashboardByAdjusterDTO` — callers do not need
 *          to access `.data` again.
 */
export async function getAdjusterDashboard(): Promise<ResponseDashboardByAdjusterDTO> {
  const response = await apiClient.get<ApiResponse<ResponseDashboardByAdjusterDTO>>(
    '/api/v1/Dashboard/GetByAdjuster',
  );
  return response.data.data;
}

// ---------------------------------------------------------------------------
// Customer dashboard types — field names match DashboardDTOs.cs exactly.
// Preserved typo: "roadsideAssistence" (lowercase 's', "ence" suffix) — this
// differs from Admin's "roadSideAssintence"; both are preserved verbatim.
// ---------------------------------------------------------------------------

export interface DashboardMainByCustomerDTO {
  unreadMessages: number;
  openClaims: number;
  openDocumentRequest: number;
  pendingDocumentRequest: number;
  pendingDocumentRequestLast7Days: number;
}

export interface DashboardWeekByCustomerDTO {
  newClaims: number;
  closedClaims: number;
  towRequest: number;
  /** Preserved typo from backend — wire field is "roadsideAssistence". */
  roadsideAssistence: number;
}

export interface ResponseDashboardByCustomerDTO {
  startDate: string;
  /** Nullable on this DTO — mirrors TowProvider's and Adjuster's pattern. */
  endDate: string | null;
  main: DashboardMainByCustomerDTO;
  week: DashboardWeekByCustomerDTO;
  /**
   * Claims filed per day over the last 7 days, oldest first. Same wire shape as
   * the admin dashboard's `driverActivity`, so both render with one component.
   *
   * Optional: a Cloud Run revision older than the one that added it omits the
   * field entirely, and the customer dashboard should degrade to no chart
   * rather than crash.
   */
  claimActivity?: DriverActivityByDayDTO[];
}

/**
 * GET /api/v1/Dashboard/GetByCustomer
 *
 * No query params. Requires Bearer auth (attached automatically by the
 * apiClient interceptor). Backend restricts this route to CUSTOMER userType.
 *
 * @returns Unwrapped `ResponseDashboardByCustomerDTO` — callers do not need
 *          to access `.data` again.
 */
export async function getCustomerDashboard(): Promise<ResponseDashboardByCustomerDTO> {
  const response = await apiClient.get<ApiResponse<ResponseDashboardByCustomerDTO>>(
    '/api/v1/Dashboard/GetByCustomer',
  );
  return response.data.data;
}
